import { type AnyRouter, createLink, type LinkComponentProps, type RegisteredRouter } from '@tanstack/react-router';
import * as React from 'react';

/** Props a link-mode component rejects, each typed as the error sentence the compiler should show. */
export type LinkModeForbidden = {
    onClick?: 'onClick is not allowed with to: the link navigates by itself. Track in a button that calls navigate().';
    href?: 'href is not allowed with to: use to for app routes, or Link href for external URLs.';
};

/** The link props a plain-mode component doesn't take: without `to` there is nothing to link to. */
export type NoLinkProps = {
    to?: never;
    params?: never;
    search?: never;
    hash?: never;
    state?: never;
};

/** What a `to` of type `string` (a variable, say) is checked against: a route literal is required. */
export type WideToError = {
    to: 'to must be a route literal: write to="/…" with params, or wrap the options in linkOptions().';
};

/** Link mode for a route-literal `to`, plain mode without; one signature, as overloads report only the last error. */
export interface Linkable<THost, TPlainProps, TForbidden = LinkModeForbidden> {
    <
        TRouter extends AnyRouter = RegisteredRouter,
        const TFrom extends string = string,
        const TTo extends string | undefined = undefined,
        const TMaskFrom extends string = TFrom,
        const TMaskTo extends string = '',
    >(
        // `styled()` and `React.ComponentProps` see the plain branch, so a styled linkable keeps its plain props.
        props: [TTo] extends [string]
            ? string extends TTo
                ? WideToError
                : LinkComponentProps<THost, TRouter, TFrom, TTo, TMaskFrom, TMaskTo> & TForbidden
            : TPlainProps & NoLinkProps,
    ): React.ReactElement;
    displayName?: string;
}

export type LinkableOptions<TAllowOnClick extends boolean = boolean> = {
    /** Rendered in link mode instead of the host, e.g. `MuiMenuItem` with `component="a"`; must forward its ref. */
    linkHost?: React.ElementType;
    /** Lets link mode take `onClick` (to close a menu); it runs before navigating, and `preventDefault()` cancels. */
    allowOnClick?: TAllowOnClick;
};

/** What link mode rejects: {@link LinkModeForbidden}, less `onClick` for a component made with `allowOnClick`. */
type ForbiddenProps<TAllowOnClick extends boolean> = [TAllowOnClick] extends [true]
    ? Omit<LinkModeForbidden, 'onClick'>
    : LinkModeForbidden;

type AnyProps = Record<string, unknown>;

/** Exact by default: TanStack's fuzzy match would mark every link to an ancestor, like each breadcrumb, current. */
const exactActiveOptions = { exact: true, includeSearch: false } as const;

/** A `forwardRef` or `memo` object has no `name` of its own: its render function does. */
function nameOf(component: React.JSXElementConstructor<any>): string {
    const { displayName, name, render } = component as {
        displayName?: string;
        name?: string;
        render?: { name?: string };
    };
    return displayName || name || render?.name || 'Component';
}

/**
 * Gives `Host` a link mode via TanStack's `Link`; `Host` must forward its ref and render an `<a>` given an `href`.
 * Only for a host of your own: design-system components already take `to` and get no `*Link` sibling.
 */
export function linkable<THost extends React.JSXElementConstructor<any>, TAllowOnClick extends boolean = false>(
    Host: THost,
    { linkHost }: LinkableOptions<TAllowOnClick> = {},
): Linkable<THost, React.ComponentPropsWithRef<THost>, ForbiddenProps<TAllowOnClick>> {
    const PlainHost = Host as React.ElementType;
    const Target = linkHost ?? PlainHost;

    const LinkHost = React.forwardRef<unknown, AnyProps>(function LinkHost(props, ref) {
        // A disabled link gets no `href`: it renders plain, without the role the router sets.
        if (props.href === undefined) {
            const { role: _role, ...rest } = props;
            return <Target ref={ref} {...rest} />;
        }
        return <Target ref={ref} {...props} />;
    });
    const LinkedHost = createLink(LinkHost) as React.ElementType;

    // Cast before `displayName`: in the app's tsc, manufacturing-core's `forwardRef` overload returns a plain function.
    const Component = React.forwardRef<unknown, AnyProps>(function Linkable(props, ref) {
        if (props.to !== undefined) {
            // The ref is typed as the host's element, though an `<a>` renders.
            return <LinkedHost ref={ref} {...props} activeOptions={props.activeOptions ?? exactActiveOptions} />;
        }
        return <PlainHost ref={ref} {...props} />;
    }) as unknown as Linkable<THost, React.ComponentPropsWithRef<THost>, ForbiddenProps<TAllowOnClick>>;
    Component.displayName = `Linkable(${nameOf(Host)})`;

    return Component;
}
